import { Card } from "@/components/ui/card";
import { Clock, Users, Coins, AlertCircle, ChevronRight, FileText, Video, Info, PhoneOff, MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useQuery, useMutation } from "@apollo/client/react";
import { useState } from "react";
import { GET_SESSIONS, GET_CREDITS, GET_CURRENT_USER } from "@/graphql/queries";
import { GENERATE_CONNECTION_TOKEN, END_SESSION } from "@/graphql/mutations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate, Link } from "react-router-dom";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { getAccessToken } from "@/lib/token-utils";
import { trackDashboardEvent } from "@/lib/analytics";
import { ApiErrorScreen, useHasCriticalError } from "@/components/ApiErrorScreen";

interface Session {
  id: string;
  date: string;
  duration: string;
  participants: number;
  creditsUsed: number;
  status: "COMPLETED" | "ACTIVE" | "CANCELLED";
  flowRoomName?: string;
  title?: string;
  description?: string;
  notes?: string;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const SessionHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rejoiningSessionId, setRejoiningSessionId] = useState<string | null>(null);
  const [pendingCloseSessionId, setPendingCloseSessionId] = useState<string | null>(null);
  const [closingSessionId, setClosingSessionId] = useState<string | null>(null);
  const navigate = useNavigate();

  const [generateConnectionToken] = useMutation(GENERATE_CONNECTION_TOKEN);
  const [endSession] = useMutation(END_SESSION, {
    refetchQueries: [{ query: GET_SESSIONS, variables: { limit: pageSize, offset: (currentPage - 1) * pageSize } }],
  });

  // Get organization ID and current user info from user's organizations
  const { data: userData, loading: userLoading, error: userError } = useQuery<{ me?: { name?: string; email?: string; organizations?: { id: string; name: string; role: string; isPersonal?: boolean }[] } | null }>(GET_CURRENT_USER);
  const currentUser = userData?.me || {};
  const organizations = userData?.me?.organizations || [];
  // Use shared hook to get current organization consistently
  const { currentOrganizationId } = useCurrentOrganization(organizations);
  const organizationId = currentOrganizationId || null;
  
  const offset = (currentPage - 1) * pageSize;
  
  const { data: sessionsData, loading: sessionsLoading, error: sessionsError, refetch: refetchSessions } = useQuery(GET_SESSIONS, {
    variables: {
      limit: pageSize,
      offset: offset,
    },
    errorPolicy: "all",
  });

  const { data: creditsData, loading: creditsLoading, error: creditsError } = useQuery(GET_CREDITS, {
    errorPolicy: "all",
  });

  const sessions: Session[] = ((sessionsData as { sessions?: Session[] })?.sessions) || [];
  const totalCreditsUsed = ((creditsData as { credits?: { usedThisMonth?: number } })?.credits?.usedThisMonth) || 0;
  const queryErrors = [userError, sessionsError, creditsError];
  const hasCriticalErrors = useHasCriticalError(queryErrors);

  // Determine if there are more pages
  // If we got fewer results than the page size, we're on the last page
  const hasMorePages = sessions.length === pageSize;
  const hasPreviousPage = currentPage > 1;

  // Reset to page 1 when page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of the card when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show loading state
  if (userLoading || sessionsLoading || creditsLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-6 bg-card border-border">
          <div className="text-center text-muted-foreground">Loading sessions...</div>
        </Card>
      </div>
    );
  }

  // Show blocking error state
  if (hasCriticalErrors) {
    return (
      <ApiErrorScreen 
        errors={queryErrors}
        context="Dashboard - Session History"
      />
    );
  }


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show:</span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Alert className="bg-blue-500/10 border-blue-500/30">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-sm text-muted-foreground">
          <strong>Note:</strong> When a room ends, it takes approximately 1-2 mins to clean up and calculate credits. You will be able to see the credit transaction in the <Link to="/credits" className="text-primary hover:underline font-semibold">credits</Link> page shortly.
        </AlertDescription>
      </Alert>

      <Card className="p-6 bg-card border-border">
        {sessions.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No sessions found. Start your first session to see history here.
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {sessions.map((session) => {
                const hasMetadata = session.title || session.description || session.notes;
                
                return (
                  <div
                    key={session.id}
                    onClick={() => navigate(`/history/${session.id}`)}
                    className="p-6 rounded-lg bg-muted/30 border border-border hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1 space-y-3">
                        {/* Title and Status Row */}
                        <div className="flex items-center gap-3 flex-wrap">
                          {session.title ? (
                            <>
                              <h3 className="font-semibold text-lg">{session.title}</h3>
                              <Badge 
                                variant={session.status === "COMPLETED" ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {session.status.toLowerCase()}
                              </Badge>
                              {hasMetadata && (
                                <FileText className="w-4 h-4 text-primary" />
                              )}
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-base">{session.date}</span>
                              <Badge 
                                variant={session.status === "COMPLETED" ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {session.status.toLowerCase()}
                              </Badge>
                            </>
                          )}
                        </div>
                        
                        {/* Date (when title exists) */}
                        {session.title && (
                          <p className="text-sm text-muted-foreground">{session.date}</p>
                        )}
                        
                        {/* Description */}
                        {session.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{session.description}</p>
                        )}
                        
                        {/* Stats Row */}
                        <div className="flex items-center gap-6 text-sm text-muted-foreground pt-1">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{session.duration}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>{session.participants} {session.participants === 1 ? 'participant' : 'participants'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Credits and Action */}
                      <div className="flex items-center gap-4 flex-shrink-0">
                        {session.status !== "ACTIVE" && (
                          <div className="text-right">
                            <div className="flex items-center gap-2 justify-end mb-1">
                              <Coins className="w-5 h-5 text-primary" />
                              <span className="font-bold text-xl">{session.creditsUsed}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">credits</div>
                          </div>
                        )}
                        {session.status === "ACTIVE" && session.flowRoomName ? (
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={async (e) => {
                                e.stopPropagation();
                                setRejoiningSessionId(session.id);
                                trackDashboardEvent('session_action', {
                                  action: 'join_attempt',
                                  session_id: session.id,
                                  room_name: session.flowRoomName,
                                  source: 'session_history',
                                });
                                try {
                                  const { data } = await generateConnectionToken({
                                    variables: {
                                      roomName: session.flowRoomName,
                                      participantName: currentUser.name || currentUser.email || 'User',
                                    },
                                  });

                                  if ((data as { generateConnectionToken?: unknown })?.generateConnectionToken) {
                                    const authToken = getAccessToken();
                                    const getLivestreamUrl = () => {
                                      const baseUrl = typeof window !== 'undefined' &&
                                        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
                                        ? `http://localhost:3001/${session.flowRoomName}`
                                        : `https://meet.flowlabs.live/${session.flowRoomName}`;

                                      if (authToken) {
                                        return `${baseUrl}?token=${encodeURIComponent(authToken)}`;
                                      }
                                      return baseUrl;
                                    };

                                    trackDashboardEvent('session_action', {
                                      action: 'join_success',
                                      session_id: session.id,
                                      room_name: session.flowRoomName,
                                      source: 'session_history',
                                    });
                                    window.open(getLivestreamUrl(), '_blank');
                                  }
                                } catch (error) {
                                  console.error('Failed to rejoin session:', error);
                                  trackDashboardEvent('session_action', {
                                    action: 'join_failure',
                                    session_id: session.id,
                                    room_name: session.flowRoomName,
                                    source: 'session_history',
                                    error_message: error instanceof Error ? error.message : 'rejoin_session_failed',
                                  });
                                } finally {
                                  setRejoiningSessionId(null);
                                }
                              }}
                              disabled={rejoiningSessionId === session.id || closingSessionId === session.id}
                              size="sm"
                              className="gap-2"
                            >
                              {rejoiningSessionId === session.id ? 'Joining...' : (
                                <>
                                  <Video className="w-4 h-4" />
                                  Rejoin
                                </>
                              )}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={closingSessionId === session.id || rejoiningSessionId === session.id}
                                  className="h-8 w-8 p-0"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingCloseSessionId(session.id);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <PhoneOff className="w-4 h-4 mr-2" />
                                  {closingSessionId === session.id ? 'Closing...' : 'Force Close'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {(hasPreviousPage || hasMorePages) && (
              <div className="mt-6 pt-4 border-t border-border">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (hasPreviousPage) {
                            handlePageChange(currentPage - 1);
                          }
                        }}
                        className={!hasPreviousPage ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                          className="cursor-pointer"
                        >
                          {currentPage - 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink isActive className="cursor-default pointer-events-none">
                        {currentPage}
                      </PaginationLink>
                    </PaginationItem>
                    {hasMorePages && (
                      <PaginationItem>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                          className="cursor-pointer"
                        >
                          {currentPage + 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (hasMorePages) {
                            handlePageChange(currentPage + 1);
                          }
                        }}
                        className={!hasMorePages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
                <div className="text-center text-sm text-muted-foreground mt-4">
                  Showing {offset + 1}-{offset + sessions.length} of sessions
                </div>
              </div>
            )}
          </>
        )}

        <div className="pt-4 border-t border-border mt-6">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Total credits used this month</span>
            <span className="text-xl font-bold text-green-500">
              {totalCreditsUsed.toLocaleString()} credits
            </span>
          </div>
        </div>
      </Card>

      <AlertDialog
        open={Boolean(pendingCloseSessionId)}
        onOpenChange={(open) => { if (!open) setPendingCloseSessionId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force close session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately end the session for all participants and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                const sessionId = pendingCloseSessionId;
                setPendingCloseSessionId(null);
                if (!sessionId) return;
                setClosingSessionId(sessionId);
                try {
                  await endSession({ variables: { sessionId } });
                } catch (error) {
                  console.error('Failed to force close session:', error);
                } finally {
                  setClosingSessionId(null);
                }
              }}
            >
              Force Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SessionHistory;
